import type { BoardClimbsQuery } from '../../types/device';

/** A grade as the board's grading system lists it, hardest last. */
export interface GradeOption {
  id: string;
  name: string;
}

/**
 * The orderings offered on a board tab.
 *
 * Grade appears as its two directions rather than as one option with a
 * direction toggle: "easiest first" and "hardest first" are what a climber
 * actually means, and a lone "by grade" leaves them guessing which way it went.
 */
export type BoardSortOption = 'created_at' | 'name' | 'grade_asc' | 'grade_desc' | 'repeats';

export const BOARD_SORTS: BoardSortOption[] = [
  'created_at',
  'name',
  'grade_asc',
  'grade_desc',
  'repeats',
];

/** How each option reaches the API, which takes the field and the direction apart. */
const SORT_QUERY: Record<BoardSortOption, Pick<BoardClimbsQuery, 'sort' | 'direction'>> = {
  created_at: { sort: 'created_at' },
  name: { sort: 'name', direction: 'asc' },
  grade_asc: { sort: 'grade', direction: 'asc' },
  grade_desc: { sort: 'grade', direction: 'desc' },
  repeats: { sort: 'repeats', direction: 'desc' },
};

export function sortQuery(sort: BoardSortOption): Pick<BoardClimbsQuery, 'sort' | 'direction'> {
  return SORT_QUERY[sort] ?? SORT_QUERY.created_at;
}

/**
 * Whether to show circuits, ordinary problems, or both.
 *
 * Tri-state rather than a switch: "circuits only" and "problems only" are both
 * things a climber walks up to a board wanting, and neither is the absence of
 * the other — a switch would have to pick one of them to be its "off".
 */
export type BoardCircuitFilter = 'all' | 'yes' | 'no';

export const BOARD_CIRCUIT_FILTERS: BoardCircuitFilter[] = [ 'all', 'yes', 'no' ];

/** How the tri-state reaches the API, which takes it as a plain boolean. */
export function circuitQuery(circuit: BoardCircuitFilter): BoardClimbsQuery['circuit'] {
  if (circuit === 'yes') return 'true';
  if (circuit === 'no') return 'false';
  return undefined;
}

/**
 * Everything a board tab filters and orders by, apart from the name — that one
 * stays out on the toolbar because it is the one people reach for constantly.
 *
 * `gradeMin`/`gradeMax` are grade ids; null at either end means "open". The
 * angle is only ever set on the boards that grade per angle.
 */
export interface BoardFilters {
  gradeMin: string | null;
  gradeMax: string | null;
  unsentOnly: boolean;
  inclination: number | null;
  circuit: BoardCircuitFilter;
  sort: BoardSortOption;
}

export const DEFAULT_BOARD_FILTERS: BoardFilters = {
  gradeMin: null,
  gradeMax: null,
  unsentOnly: false,
  inclination: null,
  circuit: 'all',
  sort: 'created_at',
};

/**
 * Filters as they come back out of storage, which may have been written by an
 * older build — an unknown sort falls back rather than reaching the API and
 * being ignored there.
 */
export function normalizeBoardFilters(stored: Partial<BoardFilters> | null): BoardFilters {
  if (!stored) return DEFAULT_BOARD_FILTERS;

  return {
    ...DEFAULT_BOARD_FILTERS,
    ...stored,
    sort: BOARD_SORTS.includes(stored.sort as BoardSortOption)
      ? (stored.sort as BoardSortOption)
      : DEFAULT_BOARD_FILTERS.sort,
    circuit: BOARD_CIRCUIT_FILTERS.includes(stored.circuit as BoardCircuitFilter)
      ? (stored.circuit as BoardCircuitFilter)
      : DEFAULT_BOARD_FILTERS.circuit,
  };
}

/** How many filters are narrowing the list — what the toolbar badge counts. */
export function activeFilterCount(filters: BoardFilters): number {
  return [
    filters.gradeMin !== null || filters.gradeMax !== null,
    filters.unsentOnly,
    filters.inclination !== null,
    filters.circuit !== 'all',
  ].filter(Boolean).length;
}

/** One thing the list is currently narrowed or ordered by, as a removable chip. */
export interface BoardFilterChip {
  key: 'grade' | 'unsent' | 'inclination' | 'circuit' | 'sort';
  /** What clearing this chip alone leaves behind. */
  cleared: Partial<BoardFilters>;
  /** Ready to show as it stands — a grade range, an angle. */
  value?: string;
  /** …or a translation key, where the value is one of a fixed set of words. */
  valueKey?: string;
}

/**
 * What is currently in force, for the chips under the toolbar — the filters
 * live behind a dialog, so without these there is nothing on screen to say why
 * the list is short.
 *
 * The ordering is in here too, when it is not the default: it is not a filter,
 * but it changes what you are looking at just as visibly.
 */
export function boardFilterChips(filters: BoardFilters, grades: GradeOption[]): BoardFilterChip[] {
  const chips: BoardFilterChip[] = [];
  const nameOf = (id: string | null) => grades.find((grade) => grade.id === id)?.name;

  // Nothing to name the range with on a board that has no scale — which a
  // stored filter from a board that has since lost its grading system can hit.
  if ((filters.gradeMin || filters.gradeMax) && grades.length > 0) {
    const from = nameOf(filters.gradeMin) ?? grades[0].name;
    const to = nameOf(filters.gradeMax) ?? grades[grades.length - 1].name;
    chips.push({
      key: 'grade',
      cleared: { gradeMin: null, gradeMax: null },
      value: from === to ? from : `${from} – ${to}`,
    });
  }

  if (filters.unsentOnly) {
    chips.push({ key: 'unsent', cleared: { unsentOnly: false }, value: '✓' });
  }

  if (filters.inclination !== null) {
    chips.push({ key: 'inclination', cleared: { inclination: null }, value: `${filters.inclination}°` });
  }

  if (filters.circuit !== 'all') {
    chips.push({
      key: 'circuit',
      cleared: { circuit: 'all' },
      valueKey: `climbs.board.circuits.${filters.circuit}`,
    });
  }

  if (filters.sort !== DEFAULT_BOARD_FILTERS.sort) {
    chips.push({
      key: 'sort',
      cleared: { sort: DEFAULT_BOARD_FILTERS.sort },
      valueKey: `climbs.board.sorts.${filters.sort}`,
    });
  }

  return chips;
}

/** Per board: a gym's Kilter and its Moonboard are filtered separately. */
export const boardFiltersStorageKey = (boardId: string) => `board_filters_${boardId}`;

/**
 * The name searched for when a problem was opened from the board tab, for the
 * session only (see BoardTabContent on why the tab itself does not remember
 * it): what lets the problem's page list the same problems the tab did.
 */
export const boardSearchStorageKey = (boardId: string) => `board_search_${boardId}`;

/**
 * What the board's problem list asks the API for, page aside: the board tab
 * and the list on a problem's page (BoardClimbsListDrawer) send the same, so
 * the two show the same problems in the same order.
 */
export function boardClimbsQuery(filters: BoardFilters, name: string): BoardClimbsQuery {
  return {
    name: name || undefined,
    grade_min: filters.gradeMin ?? undefined,
    grade_max: filters.gradeMax ?? undefined,
    sent: filters.unsentOnly ? 'false' : undefined,
    inclination: filters.inclination ?? undefined,
    circuit: circuitQuery(filters.circuit),
    ...sortQuery(filters.sort),
  };
}
